import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useReveal, revealClass } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Insights = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headRef, headVisible] = useReveal();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/insights`);
        if (mounted) setItems(res.data || []);
      } catch (e) {
        console.error("Failed to load insights", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "en" ? "en-GB" : "es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <section
      id="insights"
      data-testid="section-insights"
      className="py-24 md:py-32 border-t border-line bg-white scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={headRef}
          className={`${revealClass(headVisible)} mb-14 grid grid-cols-12 gap-y-6 md:gap-x-16`}
        >
          <div className="col-span-12 md:col-span-4">
            <p className="section-index mb-4" data-testid="insights-index">
              {t.insights.index}
            </p>
            <h2
              className="font-serif-display font-light tracking-tight text-ink text-5xl md:text-6xl leading-none"
              data-testid="insights-title"
            >
              {t.insights.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-lg text-ink-soft font-light max-w-xl" data-testid="insights-kicker">
              {t.insights.kicker}
            </p>
          </div>
        </div>

        <div className="border-t border-line" data-testid="insights-list">
          {loading && (
            <div className="py-10 text-sm text-ink-soft" data-testid="insights-loading">
              …
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="py-10 text-sm text-ink-soft" data-testid="insights-empty">
              {t.insights.empty}
            </div>
          )}
          {!loading &&
            items.map((it) => (
              <Link
                key={it.slug}
                to={`/notas/${it.slug}`}
                data-testid={`insight-item-${it.slug}`}
                className="group block border-b border-line py-8 md:py-10 transition-colors"
              >
                <div className="grid grid-cols-12 items-baseline gap-y-3 md:gap-x-8">
                  <div className="col-span-12 md:col-span-2">
                    <p className="eyebrow">
                      {lang === "en" ? it.category_en : it.category_es}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <h3 className="font-serif-display text-3xl md:text-4xl font-light text-ink leading-tight tracking-tight group-hover:text-navy transition-colors">
                      {lang === "en" ? it.title_en : it.title_es}
                    </h3>
                    <p className="mt-3 text-base text-ink-soft font-light max-w-2xl leading-relaxed">
                      {lang === "en" ? it.excerpt_en : it.excerpt_es}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-3 flex md:justify-end items-center gap-4">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(it.published_at)}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-ink-soft group-hover:text-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </section>
  );
};

export default Insights;
